import { LegalPageLayout } from '../components/LegalPageLayout'
import type { TocItem } from '../components/TableOfContents'

const tocItems: TocItem[] = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'information-we-collect', title: '1. Information We Collect' },
  { id: 'how-we-use', title: '2. How We Use Information' },
  { id: 'how-we-share', title: '3. How We Share Information' },
  { id: 'hipaa', title: '4. HIPAA & Health Information' },
  { id: 'cookies', title: '5. Cookies' },
  { id: 'data-security', title: '6. Data Security' },
  { id: 'data-retention', title: '7. Data Retention' },
  { id: 'children', title: '8. Children' },
  { id: 'location', title: '9. Location of Information' },
  { id: 'consumer-health-data', title: '10. Consumer Health Data' },
  { id: 'state-privacy-rights', title: '11. Your State Privacy Rights' },
  { id: 'glba', title: '12. Financial Information (GLBA)' },
  { id: 'third-party-links', title: '13. Third-Party Links' },
  { id: 'changes', title: '14. Changes to This Policy' },
  { id: 'contact', title: '15. Contact Us' },
]

export const Privacy = () => {
  return (
    <LegalPageLayout
      title="BENEPICKS PRIVACY POLICY"
      lastUpdated="October 5, 2026"
      tocItems={tocItems}
    >
      <section id="introduction" className="mb-10">
        <p>
          This Privacy Policy explains how Benepicks, Inc. (“Benepicks,” “we,”
          “us,” or “our”) collects, uses, shares, and protects information when
          you use our website, mobile applications, APIs, and related technology
          platform (collectively, the “Platform”). It applies to: (a) employees
          of employers who use Benepicks (“Members”) and the spouses, children,
          and other individuals whose information a Member provides or who are
          enrolled in or reimbursed for coverage through a Member
          (“Dependents”); (b) administrative contacts at our employer customers
          (“Employer Users”); (c) representatives of our broker partners and
          other service providers; and (d) visitors to our public website
          (collectively, “you”).
        </p>
        <p>
          Please read this Privacy Policy carefully. By using the Platform you
          acknowledge that you have read it. Your use of the Platform is also
          governed by the Benepicks Platform Terms and, as applicable to you,
          the Member Terms of Service, the Master Services Agreement, or the
          Broker Partner Agreement.
        </p>

        <div className="my-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <p className="text-sm font-medium text-neutral-900 mb-3">
            QUICK SUMMARY (this is not a substitute for the full policy):
          </p>
          <ul className="text-sm text-neutral-600 space-y-2 list-disc pl-5">
            <li>
              We collect personal information you give us, information from your
              employer, and basic technical information when you use the
              Platform.
            </li>
            <li>
              We use this information to maintain the Platform, route you to a
              licensed broker who enrolls you in coverage, and facilitate
              operational functions related to your benefits.
            </li>
            <li>
              We share information with the licensed broker assigned to you,
              your insurance company, a third-party money movement partner that
              handles premium payment processing, a third-party payroll API
              connector, and similar service providers, but we do NOT sell your
              information to advertisers or marketers.
            </li>
            <li>
              Some of your information is health information protected by HIPAA.
              We handle it under a Business Associate Agreement with your
              employer’s group health plan.
            </li>
            <li>
              You may have rights to access, correct, delete, or limit the use
              of your information depending on the state where you live. See
              Section 11.
            </li>
            <li>
              We retain information only as long as necessary or as required by
              law.
            </li>
            <li>
              We do not knowingly collect information from children under 18.
            </li>
          </ul>
        </div>
      </section>

      <section id="information-we-collect" className="mb-10">
        <h2>1. Information We Collect</h2>

        <h3>1.1 Information You Provide Directly</h3>
        <p>
          When you create an account, complete enrollment, or otherwise interact
          with the Platform, you may provide some or all of the following,
          including but not limited to:
        </p>
        <ul>
          <li>
            Identification and contact information: legal name, date of birth,
            residential and mailing address, email address, phone number, and
            Social Security number (SSN);
          </li>
          <li>
            Employment information: employer name, employee ID, employment
            status (full-time/part-time), hire date, and termination date;
          </li>
          <li>
            Household and financial information: household income, household
            size, tax filing status, and information about your dependents
            (including their names, dates of birth, SSNs, and addresses);
          </li>
          <li>
            Health information that you voluntarily disclose: any chronic
            conditions, ongoing medications, or other health information you
            choose to share to help select coverage; tobacco-use status; medical
            insurance coverage preferences;
          </li>
          <li>
            Payment and banking information: bank account or debit/credit card
            information used in connection with your FBO Account (defined in
            Section 3), payment instructions, and transaction history;
          </li>
          <li>
            Communications: messages, customer support requests, survey
            responses, and other content you provide.
          </li>
        </ul>

        <h3>1.2 Information We Receive From Your Employer</h3>
        <p>
          Your employer provides information about you in connection with your
          benefits, including but not limited to:
        </p>
        <ul>
          <li>
            Census data: name, DOB, SSN, address, dependents, household
            indicators, zip code, tobacco use, employment status;
          </li>
          <li>
            Payroll and deduction data, accessed via a third-party payroll API
            connector authorized by your employer;
          </li>
          <li>
            Eligibility classifications under your benefits plan (e.g., budget
            classes under a CHOICE Arrangement, formerly known as an ICHRA, or
            equivalent);
          </li>
          <li>Plan enrollment instructions or budget allocations.</li>
        </ul>

        <h3>1.3 Information We Receive From Service Providers</h3>
        <p>
          We receive information from third parties acting on our or your
          employer’s behalf:
        </p>
        <ul>
          <li>
            The licensed broker assigned to you confirms your enrollment status,
            policy details, effective dates, and any servicing updates;
          </li>
          <li>
            The money movement partner confirms premium-payment activity, FBO
            account balances, and ledger entries;
          </li>
          <li>
            Insurance companies may confirm enrollment, policy information,
            premium amounts, and renewal information;
          </li>
          <li>
            Identity verification or fraud-prevention vendors may provide
            verification results.
          </li>
        </ul>

        <h3>1.4 Information We Collect Automatically</h3>
        <p>
          When you use the Platform, we automatically collect limited technical
          information:
        </p>
        <ul>
          <li>
            Device and connection information: IP address, browser type,
            operating system, device identifiers, and language settings;
          </li>
          <li>
            Usage information: pages and features accessed, dates and times of
            access, click and tap events, and referring URLs;
          </li>
          <li>
            Log and security information: error logs, authentication events, and
            session information;
          </li>
          <li>Cookies and similar technologies as described in Section 5.</li>
        </ul>
        <p>
          <strong>How we limit what we collect:</strong> We design the Platform
          to collect only the information reasonably necessary to operate it. We
          do not sell your information to advertisers, marketers, or data
          brokers. We configure analytics and product-measurement tools to avoid
          capturing sensitive personal information (such as Social Security
          number, financial account information, or health information) in
          form-field content.
        </p>
      </section>

      <section id="how-we-use" className="mb-10">
        <h2>2. How We Use Information</h2>
        <p>We use your information for the following purposes:</p>
        <ul>
          <li>
            Provide the Platform: operate, maintain, secure, troubleshoot, and
            improve the Platform;
          </li>
          <li>
            Support your benefits: support your employer in operating its
            benefits programs (such as a CHOICE Arrangement, other HRAs, or
            other health plans), including transmitting census, payroll, and
            deduction data, and collecting reimbursement requests and supporting
            documentation you submit for your employer’s review;
          </li>
          <li>
            Connect you to a licensed broker: route you to the appropriate
            licensed broker based on your location and transmit the information
            the broker needs to recommend and enroll you in coverage;
          </li>
          <li>
            Facilitate premium funding: provide funding instructions to a
            third-party money movement partner that processes premium payments
            to your insurance company;
          </li>
          <li>
            Communicate with you: respond to inquiries, send account and
            security notifications, and provide regulatory disclosures;
          </li>
          <li>
            Comply with our own legal obligations: meet legal, regulatory,
            audit, tax, and recordkeeping obligations applicable to Benepicks as
            a service provider;
          </li>
          <li>
            Prevent fraud and protect security: detect, investigate, and prevent
            fraudulent, unauthorized, or unlawful activity, and protect the
            rights, property, and safety of Benepicks, our customers, and
            others;
          </li>
          <li>
            Internal analytics: understand how the Platform is used so we can
            improve product performance and reliability (this is performed using
            first-party data we already hold, not by sharing data with
            advertising networks);
          </li>
          <li>
            De-identified and aggregated information: create de-identified or
            aggregated information that does not identify you, which we may use
            and disclose for any lawful purpose (this complies with the HIPAA
            de-identification standard at 45 C.F.R. § 164.514 for any
            de-identified PHI);
          </li>
          <li>
            With your consent: any other purpose disclosed to you at the time of
            collection or to which you consent.
          </li>
        </ul>
        <p>
          <strong>AI Features.</strong> We may use artificial intelligence and
          machine-learning technologies (collectively, “AI”) to support our
          Services, including for purposes such as customer support, content
          generation, search and recommendations, internal analytics, fraud
          detection, and product improvement. We do not use Protected Health
          Information (PHI) to train AI or machine-learning models. We may use
          de-identified or aggregated information (which does not identify you)
          to develop, improve, or train our AI features in accordance with
          applicable law. When AI features are made available to you, you may
          have additional choices or controls described in those features.
        </p>
        <p>
          <strong>No Sale of Personal Information.</strong> We do not sell your
          personal information for money. To the extent any sharing of
          information for marketing, analytics, or advertising purposes may
          constitute a “sale” or “sharing” under your state’s privacy law,
          please see Section 11 for information about your rights.
        </p>
      </section>

      <section id="how-we-share" className="mb-10">
        <h2>3. How We Share Information</h2>
        <p>
          We share your information only as described in this Section 3. We do
          not sell your information.
        </p>

        <h3>3.1 With Your Employer</h3>
        <p>
          We share your information with your employer (the sponsor and
          administrator of your benefits) and its authorized representatives,
          only as needed for your employer to operate its benefits program.
          Limits on what your employer may see and use are governed by HIPAA,
          your employer’s HIPAA Notice of Privacy Practices, and applicable law.
        </p>

        <h3>3.2 With the Licensed Broker</h3>
        <p>
          We share information with the licensed insurance broker assigned to
          you to enable the broker to recommend coverage, enroll you, submit
          premium payment credentials to your insurance company, and service
          your policy. The broker is an independent third party (not Benepicks).
          To the extent the information is Protected Health Information, the
          broker handles it either as a Subcontractor Business Associate of
          Benepicks under a written agreement or as your employer’s plan’s own
          business associate under its own agreement, depending on how the
          broker is engaged. The broker also handles the information under its
          own legal obligations and policies. Categories of information shared
          with the broker include identification, contact, household, financial,
          health, and payment-credential information described in Section 1.
          Brokers may record telephone calls with you in accordance with
          applicable consent laws and their own privacy policies.
        </p>

        <h3>3.3 With the Money Movement Partner</h3>
        <p>
          Where your employer uses direct premium payment, we share
          account-funding instructions and ledger information with a third-party
          money movement partner that establishes and operates a “for benefit
          of” account (“FBO Account”) that your employer funds to pay premiums
          for its employees. Premium payments to insurance companies are
          processed through that FBO Account. Where your employer reimburses you
          through payroll instead, no FBO Account is used for you. The money
          movement partner handles your information under its own terms and
          applicable bank-partner arrangements.
        </p>

        <h3>3.4 With Insurance Companies</h3>
        <p>
          We (or the broker, on your behalf) share information with insurance
          companies to apply for, bind, and maintain coverage.
        </p>

        <h3>3.5 With the Payroll Integration Vendor</h3>
        <p>
          With your employer’s authorization, we use a third-party payroll API
          connector to read organization, employee, payroll, and deduction data
          from your employer’s payroll, HRIS, or benefits administration system.
          Your employer connects their payroll system to that connector, which
          transmits the relevant data to Benepicks through a secure API. The
          connector acts as a service provider both to your employer (which
          authorizes the connection) and to Benepicks (which engages it for the
          integration). The connector handles your information under its own
          privacy policy.
        </p>

        <h3>3.6 With Other Service Providers</h3>
        <p>
          We share information with service providers that perform services on
          our behalf, including cloud hosting (e.g., AWS or similar), security
          and monitoring, customer support, identity verification, audit,
          professional advisors, and similar providers. Service providers are
          bound by contract to use the information only as needed to provide
          services to us and to protect it appropriately.
        </p>

        <h3>3.7 For Legal and Compliance Reasons</h3>
        <p>
          We share information when we believe in good faith that disclosure is
          needed to: (a) comply with applicable law, regulation, subpoena, court
          order, or governmental request; (b) cooperate with regulators
          (including HHS-OCR, state departments of insurance, the IRS, the DOL,
          and state attorneys general); (c) enforce our agreements; (d) detect,
          prevent, or address fraud, security, or technical issues; or (e)
          protect the rights, property, or safety of Benepicks, our customers,
          or others.
        </p>

        <h3>3.8 Business Transfers</h3>
        <p>
          If Benepicks is involved in a merger, acquisition, financing, asset
          sale, reorganization, bankruptcy, or similar transaction, we may share
          or transfer information as part of that transaction. We will require
          the recipient to honor this Privacy Policy or provide you notice of
          any material change in handling.
        </p>

        <h3>3.9 With Your Direction or Consent</h3>
        <p>
          We share information for any other purpose with your direction or
          consent, including when you specifically ask us to do so.
        </p>

        <h3>3.10 No Sale of Personal Information</h3>
        <p>
          We do not sell personal information for money. Some sharing of
          information with analytics, marketing, or advertising service
          providers may be considered “sharing” or, in some interpretations, a
          “sale” under certain state privacy laws (such as the California
          Consumer Privacy Act as amended by the CPRA). Where applicable, you
          may exercise the rights described in Section 11, including the right
          to opt out of “sale” or “sharing.” We do not knowingly sell or share
          the personal information of any individual under the age of sixteen
          (16). Any Dependent information we receive from an authorized adult
          Member is used solely to operate the benefits program.
        </p>
      </section>

      <section id="hipaa" className="mb-10">
        <h2>4. HIPAA and Your Health Information</h2>
        <p>
          <strong>4.1 Business Associate Role.</strong> Some of the information
          we collect is Protected Health Information (PHI) under the Health
          Insurance Portability and Accountability Act (HIPAA). Benepicks is a
          Business Associate of your employer’s group health plan (the “Covered
          Entity”) and handles PHI under a Business Associate Agreement with
          your employer.
        </p>
        <p>
          <strong>4.2 Primary Notice.</strong> Your employer (or its group
          health plan) is responsible for providing you with the official HIPAA
          Notice of Privacy Practices, which describes your HIPAA rights in
          detail. Please refer to that notice for full information.
        </p>
        <p>
          <strong>4.3 Benepicks’ HIPAA Practices.</strong> Benepicks uses and
          discloses PHI only as permitted by HIPAA and the applicable Business
          Associate Agreement, including for treatment, payment, and health care
          operations of the group health plan; for our proper management and
          administration; to perform our services; for data aggregation services
          to the Covered Entity; to de-identify PHI; and as required by law. We
          maintain administrative, physical, and technical safeguards in
          accordance with the HIPAA Security Rule (45 C.F.R. Part 164, Subpart
          C).
        </p>
        <p>
          <strong>4.4 Breach Notification.</strong> If a breach of unsecured PHI
          involving your information occurs at Benepicks, we will notify your
          employer’s group health plan (and, where applicable, you and HHS) as
          required by HIPAA.
        </p>
      </section>

      <section id="cookies" className="mb-10">
        <h2>5. Cookies and Similar Technologies</h2>
        <p>
          We and our service providers use cookies, pixels, tags, software
          development kits (SDKs), local storage, and similar technologies
          (collectively, “Cookies”) on the Platform. Cookies help us operate the
          Platform, remember your preferences, understand how the Platform is
          used, and support our marketing efforts.
        </p>
        <p>The categories of Cookies we may use include:</p>
        <ul>
          <li>
            Strictly necessary: authentication, session management, security,
            fraud prevention, and load balancing. Without these, the Platform
            will not function;
          </li>
          <li>Functional: remembering your preferences and settings;</li>
          <li>
            Performance and analytics: helping us understand how visitors and
            users interact with the Platform so we can improve performance,
            reliability, and the user experience (this may include first-party
            or third-party analytics services);
          </li>
          <li>
            Marketing and advertising (public marketing website only): on our
            public marketing pages (not in your authenticated account),
            supporting B2B marketing campaigns to potential employer customers,
            measuring effectiveness, and showing relevant content. We do not use
            marketing or advertising Cookies in your authenticated account, on
            pages where Members provide PHI or sensitive personal information,
            or in any manner that targets identified Members based on Protected
            Health Information or sensitive personal information.
          </li>
        </ul>
        <p>
          <strong>Your choices.</strong> Most browsers allow you to control
          Cookies through their settings, including the ability to refuse or
          delete them. Disabling some Cookies may affect Platform functionality.
          Where applicable law requires opt-in consent or a
          preference-management tool for certain Cookie categories, we will
          provide one through the Platform.
        </p>
        <p>
          <strong>Do Not Track / Global Privacy Control (GPC).</strong> Some
          browsers transmit a “Do Not Track” signal. Because there is no
          industry-standard interpretation of Do Not Track signals, we do not
          respond to them at this time. If you send a valid Global Privacy
          Control (GPC) signal that we recognize, we will treat it as a request
          to opt out of any “sale” or “sharing” of personal information for
          cross-context behavioral advertising, where applicable under your
          state’s law.
        </p>
      </section>

      <section id="data-security" className="mb-10">
        <h2>6. Data Security</h2>
        <p>
          We use administrative, physical, and technical safeguards designed to
          protect your information, including:
        </p>
        <ul>
          <li>Encryption of data in transit and at rest;</li>
          <li>Independent third-party security assessments;</li>
          <li>An incident response plan;</li>
          <li>Security training for personnel;</li>
          <li>Vulnerability assessments.</li>
        </ul>
        <p>
          <strong>No system is perfectly secure.</strong> Despite our
          safeguards, no method of transmission or storage is one hundred
          percent secure, and we cannot guarantee absolute security. You are
          responsible for keeping your account credentials confidential. If you
          believe your account has been compromised, contact us immediately at{' '}
          <a href="mailto:support@benepicks.com">support@benepicks.com</a>.
        </p>
      </section>

      <section id="data-retention" className="mb-10">
        <h2>7. Data Retention</h2>
        <p>
          We retain personal information only for as long as necessary to
          provide the Platform, fulfill the purposes described in this Privacy
          Policy, and comply with applicable legal, tax, audit, and regulatory
          recordkeeping requirements. The criteria we use to determine retention
          periods include the type of information, the purpose for which it was
          collected, applicable legal and contractual obligations, and the need
          to resolve disputes and maintain security. Protected Health
          Information is retained and returned or destroyed as provided in the
          applicable Business Associate Agreement.
        </p>
        <p>
          When the retention period ends, we will delete, destroy, or
          de-identify the information, except where law requires longer
          retention or where we are subject to a legal hold.
        </p>
      </section>

      <section id="children" className="mb-10">
        <h2>8. Children</h2>
        <p>
          The Platform is not intended for use by children under the age of
          eighteen (18), and we do not knowingly collect personal information
          directly from children. Information about a Dependent who is a child
          may be provided by an authorized adult Member for the purpose of
          enrolling the child in coverage. Dependents do not use the Platform.
          If you believe we have inadvertently collected information directly
          from a child under 18, please contact{' '}
          <a href="mailto:privacy@benepicks.com">privacy@benepicks.com</a> and
          we will promptly delete it.
        </p>
      </section>

      <section id="location" className="mb-10">
        <h2>9. Location of Information; U.S. Users Only</h2>
        <p>
          Benepicks operates in the United States. Your information is stored
          and processed in the United States. We do not knowingly transfer
          personal information outside the United States. The Platform is
          intended for use by U.S. residents only. If you access the Platform
          from outside the United States, you do so at your own initiative and
          are responsible for compliance with local laws. By using the Platform,
          you consent to the transfer and processing of your information in the
          United States, which may have different data-protection rules than
          your country.
        </p>
      </section>

      <section id="consumer-health-data" className="mb-10">
        <h2>10. Consumer Health Data (Washington, Nevada, and Similar Laws)</h2>
        <p>
          Some of the information we collect may qualify as “consumer health
          data” under Washington’s My Health My Data Act (MHMDA), Nevada SB 220
          / NRS 603A.500, or similar laws. This section provides specific
          disclosures required by those laws.
        </p>

        <h3>10.1 Categories of Consumer Health Data We Collect</h3>
        <ul>
          <li>
            Information that identifies your past, present, or future physical
            or mental health status, including chronic-condition disclosures,
            medication-related information, and tobacco-use status;
          </li>
          <li>
            Information that identifies your seeking of health-care services,
            including insurance coverage choices and benefit utilization;
          </li>
          <li>
            Information derived or extrapolated from non-health information for
            the purpose of identifying or inferring your health status (we do
            not knowingly do this, but the law requires that we disclose this
            category).
          </li>
        </ul>

        <h3>10.2 Sources</h3>
        <p>
          Directly from you, from your employer, and from the licensed broker
          assigned to you.
        </p>

        <h3>10.3 Purposes</h3>
        <p>
          To provide the Platform, support your benefits, connect you with the
          broker, facilitate premium funding through a third-party money
          movement partner, comply with our own legal obligations, and as
          otherwise described in Section 2 of this Privacy Policy.
        </p>

        <h3>10.4 Sharing of Consumer Health Data</h3>
        <p>
          We share consumer health data only with the categories of recipients
          described in Section 3 (your employer, the licensed broker, the money
          movement partner, insurance companies, the payroll API connector,
          other service providers, and as required by law). We do not sell
          consumer health data. Where applicable law requires consent before any
          specific processing or sharing of consumer health data, we will obtain
          that consent.
        </p>

        <h3>10.5 Your Rights Regarding Consumer Health Data</h3>
        <p>
          Washington residents, and where applicable Nevada residents, may: (a)
          confirm whether we are processing consumer health data about them; (b)
          access the data; (c) withdraw consent to processing; and (d) request
          deletion. Contact{' '}
          <a href="mailto:privacy@benepicks.com">privacy@benepicks.com</a> to
          exercise these rights. We may need to verify your identity and may
          decline requests where retention is required by law or necessary to
          support your benefits.
        </p>
      </section>

      <section id="state-privacy-rights" className="mb-10">
        <h2>11. Your State Privacy Rights</h2>
        <p>
          Depending on the state where you live, you may have specific rights
          under state privacy laws, including the California Consumer Privacy
          Act (CCPA) as amended by the CPRA, the Virginia Consumer Data
          Protection Act (VCDPA), the Colorado Privacy Act (CPA), the
          Connecticut Data Privacy Act (CTDPA), the Utah Consumer Privacy Act
          (UCPA), the Texas Data Privacy and Security Act (TDPSA), the Oregon
          Consumer Privacy Act (OCPA), the Montana Consumer Data Privacy Act
          (MCDPA), and similar laws.
        </p>

        <h3>11.1 Rights You May Have</h3>
        <ul>
          <li>
            Right to know / access: confirm whether we are processing your
            personal information and obtain a copy;
          </li>
          <li>
            Right to correct: request correction of inaccurate information;
          </li>
          <li>
            Right to delete: request deletion of information we hold about you,
            subject to legal-retention and other exceptions;
          </li>
          <li>
            Right to portability: receive your information in a structured,
            commonly used, machine-readable format;
          </li>
          <li>
            Right to opt out of “sale” or “sharing” for targeted advertising: to
            the extent any of our practices qualify as a “sale” or “sharing”
            under your state’s privacy law, you may opt out as described in
            Section 11.2;
          </li>
          <li>
            Right to opt out of profiling for legally significant decisions: we
            do not engage in profiling that produces legal or similarly
            significant effects;
          </li>
          <li>
            Right to limit use of sensitive personal information: California
            residents may direct us to limit the use of sensitive personal
            information (such as SSN, financial account information, and health
            information) to that which is necessary to perform the services or
            for permitted business purposes. We already limit our use of
            sensitive personal information to those purposes;
          </li>
          <li>
            Right to non-discrimination: we will not discriminate against you
            for exercising any of these rights.
          </li>
        </ul>

        <h3>11.2 How to Exercise Your Rights</h3>
        <p>
          Email <a href="mailto:privacy@benepicks.com">privacy@benepicks.com</a>{' '}
          with your request, the state in which you reside, and reasonable
          information to verify your identity. You may also designate an
          authorized agent to make a request on your behalf; we will verify the
          agent’s authorization.
        </p>

        <h3>11.3 When Benepicks Is a Service Provider or Processor</h3>
        <p>
          For certain personal information we collect (including PHI we receive
          in our capacity as a HIPAA Business Associate, and personal
          information we process on behalf of your employer), Benepicks acts as
          a service provider or processor rather than as the controller of the
          information. In those cases, your employer (or the applicable health
          plan or other controller) is responsible for responding to many
          privacy rights requests, and we may direct your request to them. Where
          Benepicks is the controller of personal information (for example,
          information you provide directly to Benepicks for your own account),
          we will respond to your request directly.
        </p>

        <h3>11.4 Verification</h3>
        <p>
          To protect your information, we will verify requests using information
          you have already provided to us. We may require additional
          verification for sensitive requests (such as deletion of information).
          We will respond within the time required by your state’s law
          (typically 45 days, with the possibility of a 45-day extension).
        </p>

        <h3>11.5 Appeals</h3>
        <p>
          If we deny your request and you are a resident of a state that
          requires us to offer an appeal (such as Virginia, Colorado,
          Connecticut, Texas, Oregon, or Montana), you may appeal by replying to
          our response or by emailing{' '}
          <a href="mailto:privacy@benepicks.com">privacy@benepicks.com</a> with
          “Appeal” in the subject line. If we deny your appeal, we will inform
          you of the right to contact your state attorney general.
        </p>

        <h3>11.6 California “Shine the Light”</h3>
        <p>
          California Civil Code § 1798.83 permits California residents to
          request information about disclosures of personal information to third
          parties for direct marketing purposes. We do not disclose personal
          information for third-party direct marketing.
        </p>

        <h3>11.7 California CCPA Categories Disclosure (Last 12 Months)</h3>
        <p>
          During the prior twelve (12) months, we have collected, used, and
          disclosed the following categories of personal information under
          California law:
        </p>
        <table>
          <thead>
            <tr>
              <th>CCPA Category</th>
              <th>Collected?</th>
              <th>Disclosed for Business Purpose?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Identifiers (name, address, IP, SSN)</td>
              <td>Yes</td>
              <td>
                Yes: to employer, broker, payment processor, insurers, payroll
                API connector, service providers
              </td>
            </tr>
            <tr>
              <td>Customer Records (Cal. Civ. Code § 1798.80)</td>
              <td>Yes</td>
              <td>Yes: same recipients</td>
            </tr>
            <tr>
              <td>Protected Classifications</td>
              <td>Limited (DOB, dependent age, disclosed health conditions)</td>
              <td>
                Yes: for benefits eligibility and as required for enrollment
              </td>
            </tr>
            <tr>
              <td>Commercial / Transaction Information</td>
              <td>Yes: payment & enrollment</td>
              <td>Yes: same recipients</td>
            </tr>
            <tr>
              <td>Internet/Network Activity</td>
              <td>Yes: limited</td>
              <td>Yes: service providers</td>
            </tr>
            <tr>
              <td>Geolocation</td>
              <td>
                Yes: ZIP code from employer-provided census data; approximate
                IP-based location for web visits
              </td>
              <td>
                Yes: service providers and as needed for benefits eligibility
              </td>
            </tr>
            <tr>
              <td>Sensory Information</td>
              <td>
                Not collected by Benepicks (brokers may record calls separately
                under their own terms)
              </td>
              <td>N/A for Benepicks</td>
            </tr>
            <tr>
              <td>Professional/Employment</td>
              <td>Yes</td>
              <td>Yes: employer and benefits</td>
            </tr>
            <tr>
              <td>Education Information</td>
              <td>No</td>
              <td>N/A</td>
            </tr>
            <tr>
              <td>Inferences</td>
              <td>Limited (eligibility classifications)</td>
              <td>Yes: same recipients</td>
            </tr>
            <tr>
              <td>Sensitive Personal Information</td>
              <td>Yes (SSN, financial, health)</td>
              <td>Yes: same recipients, limited to providing the service</td>
            </tr>
          </tbody>
        </table>
        <p>
          <strong>
            Sold or Shared for Cross-Context Behavioral Advertising?
          </strong>{' '}
          We do not sell personal information for money. We do not share
          personal information for cross-context behavioral advertising. We have
          not sold or shared personal information in the prior twelve (12)
          months. To the extent any sharing with analytics service providers may
          be considered “sharing” under your state’s privacy law, you may opt
          out as described in Section 11.2.
        </p>
      </section>

      <section id="glba" className="mb-10">
        <h2>12. Notice for Financial Information (GLBA)</h2>
        <p>
          Because we receive financial information (including bank account,
          payment card, household income, and Social Security number
          information) in connection with the Platform, certain provisions of
          the federal Gramm-Leach-Bliley Act may apply. This Section 12 serves
          as our financial-privacy notice.
        </p>
        <p>
          <strong>What information we collect:</strong> Information from you
          (name, address, SSN, household income), from your employer (payroll
          information), from your transactions with us and others (payment
          information), and from service providers.
        </p>
        <p>
          <strong>How we use and share it:</strong> To provide the Platform,
          support your benefits, connect you with the broker, facilitate premium
          funding through a third-party money movement partner, comply with our
          own legal obligations, and as otherwise described in this Privacy
          Policy. We do not share nonpublic personal information with
          non-affiliated third parties except as permitted under GLBA (including
          to perform services, process transactions, comply with law, or with
          your consent).
        </p>
        <p>
          <strong>Right to opt out:</strong> To the extent we share nonpublic
          personal information with non-affiliated third parties in a manner
          that triggers a GLBA opt-out right (such as certain marketing-related
          sharing), we will provide an opt-out mechanism as required by GLBA.
          Currently, our sharing of nonpublic personal information is described
          in Section 3 and is undertaken to provide the Platform, support your
          benefits, or as otherwise permitted under GLBA.
        </p>
        <p>
          <strong>How we protect it:</strong> As described in Section 6 (Data
          Security).
        </p>
      </section>

      <section id="third-party-links" className="mb-10">
        <h2>13. Third-Party Links and Services</h2>
        <p>
          The Platform may contain links to or integrations with third-party
          websites, applications, or services (such as your insurance company’s
          portal, your broker’s portal, or your payroll provider’s portal). We
          are not responsible for the privacy practices of those third parties.
          Your interactions with them are governed by their own privacy
          policies.
        </p>
      </section>

      <section id="changes" className="mb-10">
        <h2>14. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. The “Last
          Updated” date at the top reflects the most recent revision. We will
          notify you of material changes by email or a notice in the Platform
          before they take effect. Non-material changes take effect when posted.
          If you do not agree to a material change, your remedy is to stop using
          the Platform before the change takes effect. The detailed modification
          mechanics are set forth in the Benepicks Platform Terms. Prior
          versions are available on request to{' '}
          <a href="mailto:privacy@benepicks.com">privacy@benepicks.com</a>.
        </p>
      </section>

      <section id="contact" className="mb-10">
        <h2>15. Contact Us</h2>
        <p>
          If you have questions, concerns, or requests about this Privacy Policy
          or our information practices, contact:
        </p>
        <p>
          Privacy questions, requests, and appeals:{' '}
          <a href="mailto:privacy@benepicks.com">privacy@benepicks.com</a>
        </p>
        <p>
          General support:{' '}
          <a href="mailto:support@benepicks.com">support@benepicks.com</a>
        </p>
        <p>
          If you are not satisfied with our response, you may have the right to
          contact the data-protection authority in your jurisdiction (for
          example, the California Attorney General’s office, the Washington
          Attorney General’s office, or the Federal Trade Commission).
        </p>
      </section>
    </LegalPageLayout>
  )
}
